'use client';

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/cn';

const inputVariants = cva(
	'w-full bg-transparent border-none outline-none text-neutral placeholder:text-neutral-weak disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-neutral/5 rounded-xl px-4 py-3',
				ghost: 'bg-transparent',
				sharing: 'rounded-md border border-border-muted bg-background px-3 py-2 text-[13px] focus:border-accent'
			},
			inputSize: {
				default: 'text-[0.9rem]',
				sm: 'text-[0.85rem] px-3 py-2',
				// Inline heading field — a dialog/panel's own name, typed in place
				// (responsibility dialog). Pair with `variant="ghost"` so the field
				// reads as the heading itself rather than a boxed input.
				title: 'text-[20px] font-semibold leading-tight'
			},
			// Reserves room on the right for a trailing adornment (e.g. the
			// PasswordInput show/hide toggle) so long values never run under it.
			trailingSpace: {
				true: 'pr-11'
			},
			// Reserves room on the left for a `leading` adornment. Set automatically
			// when `leading` is passed — never hand it in yourself. The value is the
			// field's own left padding + a 16px icon + an 8px gap, so the compound
			// variants below track whatever padding each variant/size pair resolves
			// to (`inputSize: 'sm'` re-declares px-3 and wins over the variant).
			leadingSpace: {
				true: 'pl-10'
			},
			// Failed validation - the same danger hairline as `Textarea`. Pair it
			// with a `FieldError` line under the field.
			invalid: {
				true: 'ring-1 ring-inset ring-danger/60'
			}
		},
		compoundVariants: [
			// px-3 fields (any variant at the sm size, `sharing` at any size).
			{ leadingSpace: true, inputSize: 'sm', class: 'pl-9' },
			{ leadingSpace: true, variant: 'sharing', inputSize: 'default', class: 'pl-9' },
			// `ghost` at the default size carries no horizontal padding at all.
			{ leadingSpace: true, variant: 'ghost', inputSize: 'default', class: 'pl-6' }
		],
		defaultVariants: {
			variant: 'default',
			inputSize: 'default'
		}
	}
);

/**
 * Where the leading adornment sits: flush with the field's own left padding, so
 * it lines up with the text it replaces rather than floating in the corner.
 * Mirrors the padding table in `inputVariants` — keep the two in step.
 */
const leadingAdornmentVariants = cva(
	'pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center justify-center text-neutral-muted [&_svg]:size-4',
	{
		variants: {
			variant: {
				default: 'left-4',
				ghost: 'left-0',
				sharing: 'left-3'
			},
			// Only `sm` moves the inset; the others defer to the variant above.
			inputSize: {
				default: '',
				sm: 'left-3',
				title: ''
			}
		},
		defaultVariants: {
			variant: 'default',
			inputSize: 'default'
		}
	}
);

export interface InputProps
	extends InputHTMLAttributes<HTMLInputElement>,
		Omit<VariantProps<typeof inputVariants>, 'leadingSpace' | 'invalid'> {
	/**
	 * Decorative node rendered INSIDE the field, vertically centered in its left
	 * padding — a status icon, not a control. It is `pointer-events-none` (a
	 * click lands in the input) and inherits `text-neutral-muted`; nested svgs
	 * are sized to 16px. Passing it turns on the matching `leadingSpace` text
	 * indent and wraps the input in a `relative` container; without it the
	 * component renders the exact same bare `<input>` it always has.
	 *
	 * Style the node itself for state (e.g. `className="opacity-40
	 * transition-opacity"` while a value is being resolved).
	 */
	leading?: ReactNode;
	/**
	 * The value failed validation: the field wears the danger hairline and
	 * reports `aria-invalid`. Say what is wrong in a `FieldError` under it and
	 * point `aria-describedby` at that line.
	 */
	invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
	({ className, variant, inputSize, trailingSpace, leading, invalid, ...props }, ref) => {
		const input = (
			<input
				className={cn(
					inputVariants({
						variant,
						inputSize,
						trailingSpace,
						leadingSpace: leading ? true : undefined,
						invalid: invalid || undefined,
						className
					})
				)}
				ref={ref}
				aria-invalid={invalid || undefined}
				{...props}
			/>
		);

		if (!leading) return input;

		return (
			<div className="relative w-full">
				<span className={cn(leadingAdornmentVariants({ variant, inputSize }))}>
					{leading}
				</span>
				{input}
			</div>
		);
	}
);
Input.displayName = 'Input';

export { inputVariants };
